import { LitElement, css, html, nothing } from "lit";
import type { PropertyValues, TemplateResult } from "lit";
import type { Article } from "@newsfeed/shared";
import type { ApiClient } from "../api.js";
import { ApiError } from "../api.js";
import { createArticleHandlers } from "../article-actions.js";
import { sharedStyles } from "../styles.js";
import "../components/article-list.js";

/**
 * Unified stream (all feeds) and per-feed view. Same surface, filtered by feed.
 * Newest-first ordering comes from the API.
 */
export class NfStreamView extends LitElement {
  static override properties = {
    api: { attribute: false },
    feedId: { attribute: false },
    articles: { state: true },
    filter: { state: true },
    loading: { state: true },
    error: { state: true },
    heading: { state: true },
    pending: { state: true },
  };

  api!: ApiClient;
  feedId: string | null = null;
  articles: Article[] = [];
  filter: "all" | "unread" = "all";
  loading = false;
  error = "";
  heading = "Stream";
  /** Article id → in-flight state-change action. */
  pending: Record<string, "read" | "keep"> = {};
  private handlers = createArticleHandlers(
    this.api,
    () => this.load(false),
    (id, a) => this.setPending(id, a),
  );

  static override styles = [sharedStyles, css``];

  override updated(changed: PropertyValues<this>): void {
    if (changed.has("api"))
      this.handlers = createArticleHandlers(
        this.api,
        () => this.load(false),
        (id, a) => this.setPending(id, a),
      );
    if (changed.has("api") || changed.has("feedId")) void this.load();
  }

  private async load(showLoading = true): Promise<void> {
    if (!this.api) return;
    if (showLoading) this.loading = true;
    this.error = "";
    try {
      if (this.feedId) {
        const feeds = await this.api.listFeeds();
        const feed = feeds.find((f) => f.id === this.feedId);
        this.heading = feed?.title ?? feed?.url ?? "Feed";
      } else {
        this.heading = "Stream";
      }
      this.articles = await this.api.listArticles({
        feed: this.feedId ?? undefined,
        state: this.filter === "unread" ? "unread" : undefined,
        limit: 50,
      });
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : String(err);
    } finally {
      if (showLoading) this.loading = false;
    }
  }

  private setPending(id: string, action: "read" | "keep" | null): void {
    const next = { ...this.pending };
    if (action === null) delete next[id];
    else next[id] = action;
    this.pending = next;
  }

  private onFilter(event: Event): void {
    if (event instanceof CustomEvent) {
      this.filter = event.detail.filter === "unread" ? "unread" : "all";
      void this.load();
    }
  }

  override render(): TemplateResult {
    return html`
      ${this.error ? html`<div class="card error">${this.error}</div>` : nothing}
      <nf-article-list
        .heading=${this.heading}
        .articles=${this.articles}
        .filter=${this.filter}
        .loading=${this.loading}
        .pending=${this.pending}
        .grouped=${true}
        @filter-change=${this.onFilter}
        @open-article=${this.handlers.open}
        @toggle-read=${this.handlers.read}
        @toggle-keep=${this.handlers.keep}
      ></nf-article-list>
    `;
  }
}

customElements.define("nf-stream-view", NfStreamView);

declare global {
  interface HTMLElementTagNameMap {
    "nf-stream-view": NfStreamView;
  }
}
